import { useTheme } from '@/context/theme';
import React from 'react';
import { useTranslation } from 'react-i18next';

const ThemeControls: React.FC = () => {
	const { theme, fontSize, setTheme, setFontSize, resetTheme } = useTheme();
	const { i18n } = useTranslation();

	return (
		<div className=" bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-lg max-w-50 space-y-6">
			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">
						{i18n.t('theme.schema')}
					</h4>
				</div>

				<div className="flex gap-3 max-sm:flex-col max-sm:h-44">
					<button
						className={`
						flex-1 h-20 rounded-lg border-2 transition-colors-general
						${theme === 'default'
							? 'border-blue-500 ring-2 ring-focus-ring'
							: 'border-gray-300 hover:border-brand-blue-light'
						}
						flex flex-col overflow-hidden
						focus:outline-none focus:ring-2 focus:ring-focus-ring
						`}
						onClick={() => setTheme('default')}
						aria-label={i18n.t('theme.standard')}
						aria-pressed={theme === 'default'}>
						<div className="flex-1 flex">
							<div className="flex-1 bg-[#0E2235]" />
							<div className="flex-1 bg-[#0075DC]" />
							<div className="flex-1 bg-[#F5F7FC]" />
						</div>
						<div className="text-xs py-1.5 px-2 bg-white text-gray-800 font-medium text-center">
							{i18n.t('theme.standard')}
						</div>
					</button>

					<button
						className={`
						flex-1 h-20 rounded-lg border-2 transition-colors-general
						${theme === 'contrast'
							? 'border-blue-500 ring-2 ring-focus-ring'
							: 'border-gray-300 hover:border-brand-blue-light'
						}
						flex flex-col overflow-hidden
						focus:outline-none focus:ring-2 focus:ring-focus-ring
						`}
						onClick={() => setTheme('contrast')}
						aria-label={i18n.t('theme.contrast')}
						aria-pressed={theme === 'contrast'}>
						<div className="flex-1 flex">
							<div className="flex-1 bg-black" />
							<div className="flex-1 border border-gray-300 bg-white" />
							<div className="flex-1 bg-gray-400" />
						</div>
						<div className="text-xs py-1.5 px-2 bg-white text-black font-medium text-center">
							{i18n.t('theme.contrast')}
						</div>
					</button>
				</div>
			</div>

			<div className="space-y-3">
				<h4 className="text-sm font-semibold text-gray-800 uppercase tracking-wider">
					{i18n.t('theme.size')}
				</h4>

				<div className="grid grid-cols-3 gap-2 max-sm:grid-rows-3 max-sm:grid-cols-1">
					<button
						className={`
						h-17 rounded-lg border-2 transition-colors-general
						flex flex-col items-center justify-center
						${fontSize === 'normal'
							? `border-blue-500 bg-blue-500 text-white ring-2 ring-focus-ring`
							: `border-gray-300 text-gray-800 hover:border-brand-blue-light hover:bg-gray-50`
						}
						focus:outline-none focus:ring-2 focus:ring-focus-ring
						`}
						onClick={() => setFontSize('normal')}
						aria-label={i18n.t('theme.sizes.standard')}
						aria-pressed={fontSize === 'normal'}>
						<span className="text-base font-bold">Аа</span>
						<span className="p-1 text-xs mt-1 font-medium">
							{i18n.t('theme.sizes.standard')}
						</span>
					</button>

					<button
						className={`
						h-17 rounded-lg border-2 transition-colors-general
						flex flex-col items-center justify-center
						${fontSize === 'large'
							? `border-blue-500 bg-blue-500 text-white ring-2 ring-focus-ring`
							: `border-gray-300 text-gray-800 hover:border-brand-blue-light hover:bg-gray-50`
						}
						focus:outline-none focus:ring-2 focus:ring-focus-ring
						`}
						onClick={() => setFontSize('large')}
						aria-label={i18n.t('theme.sizes.large')}
						aria-pressed={fontSize === 'large'}>
						<span className="text-lg font-bold">Аа</span>
						<span className="p-1 text-sm mt-1 font-medium">
							{i18n.t('theme.sizes.large')}
						</span>
					</button>

					<button
						className={`
						h-17 rounded-lg border-2 transition-colors-general
						flex flex-col items-center justify-center
						${fontSize === 'huge'
							? `border-blue-500 bg-blue-500 text-white ring-2 ring-focus-ring`
							: `border-gray-300 text-gray-800 hover:border-brand-blue-light hover:bg-gray-50`
						}
						focus:outline-none focus:ring-2 focus:ring-focus-ring
						`}
						onClick={() => setFontSize('huge')}
						aria-label={i18n.t('theme.sizes.huge')}
						aria-pressed={fontSize === 'huge'}>
						<span className="text-xl font-bold">Аа</span>
						<span className="p-1 text-base mt-1 font-medium">
							{i18n.t('theme.sizes.huge')}
						</span>
					</button>
				</div>
			</div>

			<div>
				<button
					className="
					w-full py-2.5 px-4 rounded-lg
					bg-gray-100 hover:bg-gray-600
					active:bg-gray-200
					text-gray-800 font-medium
					border border-gray-200
					transition-colors-general duration-200
					focus:outline-none focus:ring-2 focus:ring-focus-ring"
					onClick={resetTheme}
					aria-label={i18n.t('theme.reset')}>
					{i18n.t('theme.reset')}
				</button>
			</div>
		</div>
	);
};

export default ThemeControls;